import Breakout from 'components/Breakout';
import dayjs from 'dayjs';
import isEmpty from 'lodash/isEmpty';
import isFinite from 'lodash/isFinite';
import isString from 'lodash/isString';
import numbro from 'numbro';
import round from 'lodash/round';
import { theme } from './theme';
import trim from 'lodash/trim';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

export function applySentenceCase(str) {
    return str.replace(/.+?[\.\?\!](\s|$)/g, (txt) => {
        return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
}

export function getMean(data) {
    return (
        data.reduce((a, b) => {
            return a + b;
        }) / data.length
    );
}

export function getSD(data) {
    const m = getMean(data);
    return Math.sqrt(
        data.reduce((sq, n) => {
            return sq + (n - m) ** 2;
        }, 0) /
            (data.length - 1),
    );
}

// Assumes items contains only cat5s and brands
export const calculateData = (items) => {
    if (!items) return null;

    const safeValue = (val) => {
        if (!isFinite(val) || val === null) return null; // Return null for invalid values
        return val;
    };

    const {
        volume,
        currentMonthVolume,
        prevMonthVolume,
        currentQuarterVolume,
        prevQuarterVolume,
        currentYearVolume,
        prevYearVolume,
        predictedIncrease,
        predictedBaseVolume,
    } = items.reduce((sums, item) => {
        if (isFinite(item.volume))
            sums.volume = safeValue(sums.volume) + item.volume;
        const prevMonthVolume = item.MoM_increase / item.MoM;
        const currentMonthVolume = item.MoM_increase + prevMonthVolume;
        const prevQuarterVolume = item.QoQ_increase / item.QoQ;
        const currentQuarterVolume = item.QoQ_increase + prevQuarterVolume;
        sums.prevMonthVolume =
            safeValue(sums.prevMonthVolume) + safeValue(prevMonthVolume);
        sums.currentMonthVolume =
            safeValue(sums.currentMonthVolume) + safeValue(currentMonthVolume);
        sums.prevQuarterVolume =
            safeValue(sums.prevQuarterVolume) + safeValue(prevQuarterVolume);
        sums.currentQuarterVolume =
            safeValue(sums.currentQuarterVolume) +
            safeValue(currentQuarterVolume);
        const prevYearVolume = item.absoluteGrowth / item.YoY;
        const currentYearVolume = item.absoluteGrowth + prevYearVolume;
        sums.prevYearVolume =
            safeValue(sums.prevYearVolume) + safeValue(prevYearVolume);
        sums.currentYearVolume =
            safeValue(sums.currentYearVolume) + safeValue(currentYearVolume);
        const predictedIncrease = isFinite(item.predictedYoY)
            ? currentYearVolume * item.predictedYoY
            : NaN;
        sums.predictedIncrease =
            safeValue(sums.predictedIncrease) + safeValue(predictedIncrease);
        sums.predictedBaseVolume =
            safeValue(sums.predictedBaseVolume) +
            (isFinite(predictedIncrease) ? currentYearVolume : null);
        return sums;
    }, {});

    const calculatedValues = {
        volume,
        MoM: currentMonthVolume / prevMonthVolume - 1,
        QoQ: currentQuarterVolume / prevQuarterVolume - 1,
        YoY: currentYearVolume / prevYearVolume - 1,
        predictedYoY: predictedIncrease / predictedBaseVolume,
    };
    for (const [key, value] of Object.entries(calculatedValues)) {
        if (!isFinite(value)) calculatedValues[key] = null;
    }

    return calculatedValues;
};

export const calculateTikTokData = (items) => {
    if (!items || items.length === 0) return null;

    const safeValue = (val) => {
        if (!isFinite(val) || val === null) return null; // Return null for invalid values
        return val;
    };

    const {
        prevWeekVolume,
        currentWeekVolume,
        prevMonthVolume,
        currentMonthVolume,
        prevQuarterVolume,
        currentQuarterVolume,
        prevYearVolume,
        currentYearVolume,
        non_cum_avg_weekly_views,
    } = items.reduce((sums, item) => {
        const prevWeekVolume = item.non_cum_increase_WoW / item.non_cum_WoW;
        sums.prevWeekVolume =
            safeValue(sums.prevWeekVolume) + safeValue(prevWeekVolume);
        const currentWeekVolume = item.non_cum_increase_WoW + prevWeekVolume;
        sums.currentWeekVolume =
            safeValue(sums.currentWeekVolume) + safeValue(currentWeekVolume);
        const prevMonthVolume =
            item.non_cum_increase_MoM_rolling / item.non_cum_MoM_rolling;
        sums.prevMonthVolume =
            safeValue(sums.prevMonthVolume) + safeValue(prevMonthVolume);
        const currentMonthVolume =
            item.non_cum_increase_MoM_rolling + prevMonthVolume;
        sums.currentMonthVolume =
            safeValue(sums.currentMonthVolume) + safeValue(currentMonthVolume);
        const prevQuarterVolume =
            item.non_cum_increase_QoQ_rolling / item.non_cum_QoQ_rolling;
        sums.prevQuarterVolume =
            safeValue(sums.prevQuarterVolume) + safeValue(prevQuarterVolume);
        const currentQuarterVolume =
            item.non_cum_increase_QoQ_rolling + prevQuarterVolume;
        sums.currentQuarterVolume =
            safeValue(sums.currentQuarterVolume) +
            safeValue(currentQuarterVolume);
        const prevYearVolume =
            item.non_cum_increase_YoY_rolling / item.non_cum_YoY_rolling;
        sums.prevYearVolume =
            safeValue(sums.prevYearVolume) + safeValue(prevYearVolume);
        const currentYearVolume =
            item.non_cum_increase_YoY_rolling + prevYearVolume;
        sums.currentYearVolume =
            safeValue(sums.currentYearVolume) + safeValue(currentYearVolume);
        sums.non_cum_avg_weekly_views =
            (safeValue(sums.non_cum_avg_weekly_views) || 0) +
            safeValue(item.non_cum_avg_weekly_views);
        return sums;
    }, {});

    const results = {
        non_cum_avg_weekly_views,
        non_cum_YoY_rolling: currentYearVolume / prevYearVolume - 1,
        non_cum_QoQ_rolling: currentQuarterVolume / prevQuarterVolume - 1,
        non_cum_MoM_rolling: currentMonthVolume / prevMonthVolume - 1,
        non_cum_WoW: currentWeekVolume / prevWeekVolume - 1,
    };

    Object.keys(results).forEach((k) => {
        if (!isFinite(results[k])) results[k] = null;
    });

    return results;
};

export const calculateInstagramData = (items) => {
    if (!items || items.length === 0) return null;

    const safeValue = (val) => {
        if (!isFinite(val) || val === null) return null; // Return null for invalid values
        return val;
    };

    const {
        prevWeekVolume,
        currentWeekVolume,
        prevMonthVolume,
        currentMonthVolume,
        prevQuarterVolume,
        currentQuarterVolume,
        prevYearVolume,
        currentYearVolume,
        non_cum_avg_weekly_likes,
    } = items.reduce((sums, item) => {
        const prevWeekVolume = item.non_cum_increase_WoW / item.non_cum_WoW;
        sums.prevWeekVolume =
            safeValue(sums.prevWeekVolume) + safeValue(prevWeekVolume);
        const currentWeekVolume = item.non_cum_increase_WoW + prevWeekVolume;
        sums.currentWeekVolume =
            safeValue(sums.currentWeekVolume) + safeValue(currentWeekVolume);
        const prevMonthVolume =
            item.non_cum_increase_MoM_rolling / item.non_cum_MoM_rolling;
        sums.prevMonthVolume =
            safeValue(sums.prevMonthVolume) + safeValue(prevMonthVolume);
        const currentMonthVolume =
            item.non_cum_increase_MoM_rolling + prevMonthVolume;
        sums.currentMonthVolume =
            safeValue(sums.currentMonthVolume) + safeValue(currentMonthVolume);
        const prevQuarterVolume =
            item.non_cum_increase_QoQ_rolling / item.non_cum_QoQ_rolling;
        sums.prevQuarterVolume =
            safeValue(sums.prevQuarterVolume) + safeValue(prevQuarterVolume);
        const currentQuarterVolume =
            item.non_cum_increase_QoQ_rolling + prevQuarterVolume;
        sums.currentQuarterVolume =
            safeValue(sums.currentQuarterVolume) +
            safeValue(currentQuarterVolume);
        const prevYearVolume =
            item.non_cum_increase_YoY_rolling / item.non_cum_YoY_rolling;
        sums.prevYearVolume =
            safeValue(sums.prevYearVolume) + safeValue(prevYearVolume);
        const currentYearVolume =
            item.non_cum_increase_YoY_rolling + prevYearVolume;
        sums.currentYearVolume =
            safeValue(sums.currentYearVolume) + safeValue(currentYearVolume);
        sums.non_cum_avg_weekly_likes =
            (safeValue(sums.non_cum_avg_weekly_likes) || 0) +
            safeValue(item.non_cum_avg_weekly_likes);
        return sums;
    }, {});

    const results = {
        non_cum_avg_weekly_likes,
        non_cum_YoY_rolling: currentYearVolume / prevYearVolume - 1,
        non_cum_QoQ_rolling: currentQuarterVolume / prevQuarterVolume - 1,
        non_cum_MoM_rolling: currentMonthVolume / prevMonthVolume - 1,
        non_cum_WoW: currentWeekVolume / prevWeekVolume - 1,
    };

    Object.keys(results).forEach((k) => {
        if (!isFinite(results[k])) results[k] = null;
    });

    return results;
};

export const calculateOverviewData = (items) => {
    if (!items) return null;

    const safeValue = (val) => {
        if (!isFinite(val) || val === null) return null; // Return null for invalid values
        return val;
    };

    const {
        currentYearVolume,
        predicted_increase_YoY,
        prevYearVolume,
        prevQuarterVolume,
        currentQuarterVolume,
        prevMonthVolume,
        currentMonthVolume,
        popularity_index_avg_12_month,
    } = items.reduce((sums, item) => {
        const prevMonthVolume = item.increase_MoM / item.MoM;
        sums.prevMonthVolume =
            safeValue(sums.prevMonthVolume) + safeValue(prevMonthVolume);
        const currentMonthVolume = item.increase_MoM + prevMonthVolume;
        sums.currentMonthVolume =
            safeValue(sums.currentMonthVolume) + safeValue(currentMonthVolume);
        const prevQuarterVolume = item.increase_QoQ / item.QoQ;
        sums.prevQuarterVolume =
            safeValue(sums.prevQuarterVolume) + safeValue(prevQuarterVolume);
        const currentQuarterVolume = item.increase_QoQ + prevQuarterVolume;
        sums.currentQuarterVolume =
            safeValue(sums.currentQuarterVolume) +
            safeValue(currentQuarterVolume);
        const prevYearVolume = item.increase_YoY / item.YoY;
        sums.prevYearVolume =
            safeValue(sums.prevYearVolume) + safeValue(prevYearVolume);
        const currentYearVolume = item.increase_YoY + prevYearVolume;
        sums.currentYearVolume =
            safeValue(sums.currentYearVolume) + safeValue(currentYearVolume);
        ['predicted_increase_YoY', 'popularity_index_avg_12_month'].forEach(
            (key) => {
                const value = item[key];
                if (!isFinite(value)) return;
                sums[key] = !(key in sums) ? value : sums[key] + value;
            },
        );
        return sums;
    }, {});

    const calculatedValues = {
        popularity_index_avg_12_month,
        MoM: currentMonthVolume / prevMonthVolume - 1,
        QoQ: currentQuarterVolume / prevQuarterVolume - 1,
        YoY: currentYearVolume / prevYearVolume - 1,
        predicted_YoY: predicted_increase_YoY / currentYearVolume,
    };

    for (const [key, value] of Object.entries(calculatedValues)) {
        if (!isFinite(value)) calculatedValues[key] = null;
    }

    return calculatedValues;
};

const numbroDefaultConfig = {
    mantissa: 1,
    lowPrecision: false,
    average: true,
    negative: 'sign',
};

const numbroGrowthConfig = {
    ...numbroDefaultConfig,
    forceSign: true,
};

export const formatGrowth = (value) => {
    if (typeof value !== 'number') {
        return '-';
    }
    const percentage = value * 100;
    if (percentage >= 1000) {
        return <Breakout />;
    }
    return `${numbro(percentage).format(numbroGrowthConfig)}%`;
};

export const formatVolume = (value) =>
    typeof value === 'number' ? numbro(value).format(numbroDefaultConfig) : '-';

export const formatAbsoluteGrowth = (value) =>
    typeof value === 'number'
        ? numbro(round(value)).format(numbroGrowthConfig)
        : '-';

export const formatPercentage = (value) => {
    const formattedValue = formatGrowth(value);
    if (typeof formattedValue !== 'string') return formattedValue;
    return formattedValue.replace('+', '');
};

export const formatUnixTimestampWeek = (timestamp) =>
    dayjs.unix(timestamp).format('MMM D, YYYY');

export const roundIfFinite = (value, precision = 0) =>
    isFinite(value) ? round(value, precision) : value;

const routeParamMap = {
    cat1: 'industries',
    cat2: 'categoryTwo',
    cat3: 'categoryThree',
    cat4: 'categoryFour',
    cat5: 'categoryFive',
    brand: 'brands',
    affinity: 'related-searches',
};

export function getPathFromNode(
    { id, depth, name, type, children },
    parentId,
    dataSource,
) {
    let path;
    if (depth === 'cat1' && children && children.length === 1) {
        path = `/${routeParamMap.cat2}/${children[0]}`;
    } else if (depth === 'cat1' && !!name) {
        path = `/${routeParamMap[depth]}/${name}`;
    } else if (depth === 'cat5' && parentId && !!id) {
        path = `/${routeParamMap[depth]}/${parentId}/${id}`;
    } else if (routeParamMap[depth || type] && !!id) {
        path = `/${routeParamMap[depth || type]}/${id}`;
    } else {
        console.error(
            `not sure how to route based on depth: '${depth}' and type: '${type}'`,
        );
        path = '/';
    }
    if ((type === 'brand' || type === 'category') && dataSource) {
        path += `/${dataSource.toLowerCase()}`;
    }
    return path;
}

export function isEmptyStr(str) {
    return !isString(str) || isEmpty(str) || !trim(str);
}

export const renameCluster = (cluster) => {
    switch (cluster) {
        case 'sustained':
        case 'seasonal':
            return `${cluster} riser`;
        default:
            return cluster;
    }
};

const formatTimeSeriesHeader = (header) => {
    const formatted = {};
    if (!header) return formatted;

    return header.reduce((acc, column, idx) => {
        if (typeof column !== 'string') return acc;
        acc[column.split("'")[1].toLowerCase()] = idx;
        return acc;
    }, formatted);
};

const parseFloatAndThrowErrorForNaN = (value) => {
    const parsed = parseFloat(value);
    if (isNaN(parsed)) {
        throw new Error(`Could not parse value: ${value}`);
    }
    return parsed;
};

const tsColFormat = {
    date: (d) => (d ? d.replace(/'/g, '') : d),
    prediction: (p) => p === 'True',
    real_volume: parseFloatAndThrowErrorForNaN,
    volume: parseFloatAndThrowErrorForNaN,
};

const parseTimeSeriesValue = (key, value) => {
    const formatter = tsColFormat[key];
    if (formatter) return formatter(value);

    if (typeof value !== 'string') return value;

    const normalized = value.replace(/'/g, '');
    if (normalized === 'True') return true;
    if (normalized === 'False') return false;
    if (normalized.toLowerCase() === 'null') return null;

    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? normalized : parsed;
};

const formatTimeSeriesUnsafe = ([headerRow, ...rows]) => {
    // Copy rather than shift/mutate: callers hand us a row straight out of the
    // react-query cache, and a second pass has to see the same header.
    const header = formatTimeSeriesHeader(headerRow);
    // TODO: fix this hack! working around a data loading issue
    if (rows.length < 2) return [];
    const parsed = rows.map((row) => {
        const next = [...row];
        Object.keys(header).forEach((key) => {
            next[header[key]] = parseTimeSeriesValue(key, next[header[key]]);
        });
        return next;
    });
    return [header, ...parsed];
};

export const formatTimeSeries = (ts) => {
    try {
        return formatTimeSeriesUnsafe(ts);
    } catch (err) {
        console.error(`Error formatting time series: ${err}`);
        return [];
    }
};

export const getDataPointsKey = (...ids) => {
    const sortedIds = ids ? ids.sort().join('||') : null;
    return sortedIds ? `data||${sortedIds}` : null;
};

export const getRelatedIds = (data) =>
    Object.keys(data).reduce((acc, key) => {
        const ids = data[key];
        if (ids && ids.length && Array.isArray(ids)) acc.push(...ids);
        return acc;
    }, []);

const companyRegExp = new RegExp('^spate$', 'i');
const emailRegExp = new RegExp('@spate.nyc$', 'i');
export const isSpateUser = ({ company, email }) =>
    companyRegExp.test(company) || emailRegExp.test(email);

export const formatClusterFilter = ({ value, label }) => ({
    value,
    label: label || value,
    score: value.includes('ris')
        ? 10
        : value.includes('flat')
          ? 5
          : value.includes('no')
            ? null
            : 1,
});

export const getFormatHealthStatFilter =
    (metricLabel, { scoreLabels, scoreDefinitions }) =>
    (scoreThreshold, upperScoreThreshold) => ({
        definition: scoreDefinitions[scoreThreshold] || '',
        label: scoreLabels[scoreThreshold],
        score: scoreThreshold,
        upperScoreThreshold,
        scoreLabel: scoreLabels[scoreThreshold],
        metricLabel,
        value: `${metricLabel}-${scoreThreshold}`,
        filterSubGroup: metricLabel,
    });

// https://stackoverflow.com/a/37511463/5551706
export const removeDiacritics = (s) =>
    s.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// https://stackoverflow.com/a/6969486/5551706
export const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const removeNonAlphaNumeric = (s) => s.replace(/[^a-z,A-Z,\d]/g, '');

export const hierarchyHintObjToStr = (hierarchyHintObj) =>
    Object.values(hierarchyHintObj).join('||');

export const parseWeeklySearchTimeSeries = ({
    time_series,
    time_series_headers,
}) => {
    if (!(time_series && time_series_headers)) return null;
    const header = [
        ...time_series_headers,
        'prediction',
        'type',
        'metricLabel',
    ].reduce((acc, key, idx) => {
        acc[key] = idx;
        return acc;
    }, {});
    return [
        header,
        ...time_series.map((row) => [...row, false, 'Search', 'Search Volume']),
    ];
};

// All possible TikTok time series columns in a fixed order
// This ensures all parsed TikTok series have identical headers for consistent data access
const TIKTOK_ALL_COLUMNS = [
    'date',
    'real_volume',
    'volume',
    'prediction',
    'type',
    'metricLabel',
    // Optional columns - will be undefined if not present in the data
    'hashtags_count',
    'posts_count',
    'abs_weekly_change_rank',
    'non_cum_increase_WoW',
];

export const parseTikTokTimeSeries = (ts) => {
    const parsed = JSON.parse(
        ts.replace(/(\d{4}-\d{2}-\d{2})/g, "'$1'").replaceAll("'", '"'),
    ).map((row) => ({
        ...row,
        prediction: false,
        type: 'TikTok',
        metricLabel: 'TikTok Views',
    }));

    const header = TIKTOK_ALL_COLUMNS.reduce((acc, key, idx) => {
        acc[key] = idx;
        return acc;
    }, {});
    return [
        header,
        ...parsed.map((row) => TIKTOK_ALL_COLUMNS.map((key) => row[key])),
    ];
};

export const parseTikTokTimeSeriesQuery = (tsQuery, bugsnagClient) => {
    if (!(
        tsQuery.data &&
        tsQuery.data.data &&
        tsQuery.data.data[0] &&
        tsQuery.data.data[0].time_series
    ))
        return null;
    try {
        return parseTikTokTimeSeries(tsQuery.data.data[0].time_series);
    } catch (err) {
        bugsnagClient.notify(err);
        return null;
    }
};

export const parseAllTikTokTimeSeriesQuery = (tsQuery, bugsnagClient) => {
    if (!(
        tsQuery.data &&
        tsQuery.data.data &&
        tsQuery.data.data.length &&
        tsQuery.data.data.filter((d) => d.time_series).length
    ))
        return null;
    try {
        const parsedData = tsQuery.data.data.map((d) => ({
            ...d,
            time_series: parseTikTokTimeSeries(d.time_series),
        }));
        return parsedData;
    } catch (err) {
        bugsnagClient.notify(err);
        return null;
    }
};

const scoreColors = {
    score6: [theme.purple_500, theme.purple_700],
    score5: [theme.green_600, theme.green_700],
    score4: [theme.green_500, theme.green_600],
    score3: [theme.orange_400, theme.orange_500],
    score2: [theme.yellow_400, theme.yellow_500],
    score1: [theme.red_500, theme.red_600],
    default: [theme.gray_600, theme.gray_800],
};

const getSentimentScoreColor = (score, dark = false) => {
    switch (true) {
        case score >= 8:
            return scoreColors.score5[+dark];
        case score >= 4:
            return scoreColors.score3[+dark];
        case score > 0:
        case score === 0:
            return scoreColors.score1[+dark];
        default:
            return scoreColors.default[+dark];
    }
};

const popularityShareScoreColors = {
    score8: [theme.google_blue, theme.google_blue],
    score7: [theme.tiktok_pink, theme.tiktok_pink],
    score6: [theme.instagram_orange, theme.instagram_orange],
    default: [theme.gray_600, theme.gray_800],
};

const getPopularityShareScoreColor = (score, dark = false) => {
    switch (true) {
        case score >= 8:
            return popularityShareScoreColors.score8[+dark];
        case score >= 7:
            return popularityShareScoreColors.score7[+dark];
        case score >= 6:
            return popularityShareScoreColors.score6[+dark];
        default:
            return popularityShareScoreColors.default[+dark];
    }
};

export const getScoreColor = (score, dark = false, metricLabel) => {
    switch (true) {
        case metricLabel === 'popularity share':
            return getPopularityShareScoreColor(score, dark);
        case metricLabel === 'sentiment score':
            return getSentimentScoreColor(score, dark);
        case metricLabel === 'price point' && (score > 0 || score === 0):
            return scoreColors.score6[+dark];
        case score >= 8:
            return scoreColors.score5[+dark];
        case score >= 6:
            return scoreColors.score4[+dark];
        case score >= 4:
            return scoreColors.score3[+dark];
        case score >= 2:
            return scoreColors.score2[+dark];
        case score > 0:
        case score === 0:
            return scoreColors.score1[+dark];
        default:
            return scoreColors.default[+dark];
    }
};

export const getClusterColor = (cluster) => {
    if (!cluster) return null;
    const normalized = cluster
        .replace(/^tiktok\s+/, '')
        .replace(/^instagram\s+/, '')
        .trim();
    if (normalized.includes('riser') || normalized === 'rising star')
        return theme.green_600;
    if (normalized.includes('decliner') || normalized === 'falling star')
        return theme.red_500;
    if (normalized.includes('flat')) return theme.yellow_400;
    return null;
};

export const shapeFlavors = (hierarchies) =>
    hierarchies?.filter(([id, hierarchy]) => hierarchy.cat1 === 'flavors') ||
    null;

export const toNumberIfNumeric = (value) => {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed !== '' && !isNaN(trimmed)) {
            return Number(trimmed);
        }
    }
    return value;
};
